import { forwardRef, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';
import { InputWrapper, type InputWrapperBaseProps } from '../shared/InputBase.js';
import { TokenFieldCore, type TokenFieldOptions } from './TokenField.js';

export interface TokenInputProps
  extends InputWrapperBaseProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'type'>,
    TokenFieldOptions {
  type?: 'text' | 'search' | 'url' | 'email' | 'tel';
}

/**
 * A single-line `TextInput` that offers its `tokens` the moment the person
 * types `{{`, and paints every `{{…}}` in the value as a chip.
 *
 * A drop-in for `TextInput`: the same label, description, error and section
 * props, and the same controlled `value` / `onChange` contract, so an
 * existing call site swaps the component name and nothing else. Choosing a
 * token writes `{{token}}` into the value through an ordinary change event.
 */
export const TokenInput = forwardRef(function TokenInput(
  props: TokenInputProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const {
    label, description, error, required, withAsterisk,
    leftSection, rightSection, leftSectionWidth, rightSectionWidth,
    size, className, style,
    id, type = 'text',
    tokens, trigger, closing, fallbackHint,
    ...inputProps
  } = props;
  return (
    <InputWrapper
      label={label}
      description={description}
      error={error}
      required={required}
      withAsterisk={withAsterisk}
      leftSection={leftSection}
      rightSection={rightSection}
      leftSectionWidth={leftSectionWidth}
      rightSectionWidth={rightSectionWidth}
      size={size}
      className={className}
      style={style}
      inputId={id}
    >
      {({ inputId, describedBy, invalid, className: ctlClass }) => (
        <TokenFieldCore
          multiline={false}
          tokens={tokens}
          trigger={trigger}
          closing={closing}
          fallbackHint={fallbackHint}
          inputId={inputId}
          describedBy={describedBy}
          invalid={invalid}
          controlClass={ctlClass}
          forwardedRef={ref as Ref<HTMLInputElement | HTMLTextAreaElement>}
          controlProps={{ type, ...inputProps }}
        />
      )}
    </InputWrapper>
  );
});
